import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Spin,
  Tag,
  Row,
  Col,
  Typography,
  Modal,
  Upload,
  message,
  Card,
  Tooltip,
} from "antd";
import moment from "moment";
import {
  SearchOutlined,
  ContainerOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  InboxOutlined,
  NumberOutlined,
  BankOutlined,
  FileTextOutlined,
  LinkOutlined,
  ReloadOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "../../services/axiosInstance";
import { FETCH_ALL_RAKES, DELETE_RAKE, GET_ACTIVE_RAKE } from "../../services/apiEndPoints";
import { downloadRakeReport } from "../../services/rakeReport";
import { uploadRakeExcel } from "../../services/rakeUpload";
import { IActiveRake, IRakeMaster } from "../types/rake.types";
import ActiveRakeCard from "./ActiveRakeCard";

const { Title, Text } = Typography;

const STAT_CARDS = [
  {
    key: "total",
    label: "Total Rakes",
    icon: <ContainerOutlined />,
    color: "#1677ff",
    bg: "linear-gradient(135deg, #eff6ff, #dbeafe)",
    border: "#93c5fd",
  },
  {
    key: "companies",
    label: "Companies",
    icon: <BankOutlined />,
    color: "#8b5cf6",
    bg: "linear-gradient(135deg, #f5f3ff, #ede9fe)",
    border: "#c4b5fd",
  },
  {
    key: "thisMonth",
    label: "This Month",
    icon: <CalendarOutlined />,
    color: "#10b981",
    bg: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
    border: "#6ee7b7",
  },
  {
    key: "last7Days",
    label: "Last 7 Days",
    icon: <FileTextOutlined />,
    color: "#f59e0b",
    bg: "linear-gradient(135deg, #fffbeb, #fef3c7)",
    border: "#fcd34d",
  },
];

const RakeTableComponent = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<IRakeMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [globalSearch, setGlobalSearch] = useState("");
  const [reportLoading, setReportLoading] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeRake, setActiveRake] = useState<IActiveRake | null>(null);

  const fetchActiveRake = useCallback(async () => {
    try {
      const res = await axios.get(GET_ACTIVE_RAKE);
      const payload = res?.data;
      setActiveRake(payload?.active && payload?.data ? (payload.data as IActiveRake) : null);
    } catch {
      setActiveRake(null);
    }
  }, []);

  const performUpload = useCallback(async (file: File) => {
    setUploading(true);
    try {
      await uploadRakeExcel(file);
      message.success("Rake details uploaded successfully");
      setIsUploadModalOpen(false);
      await fetchRakes();
    } catch (error: any) {
      const serverMsg =
        error?.response?.data?.error || error?.response?.data?.message;
      if (error?.code === "ECONNABORTED") {
        message.error(
          "Upload timed out while parsing. The file may be missing RR or FNR numbers."
        );
      } else {
        message.error(serverMsg || "Failed to upload rake details");
      }
    } finally {
      setUploading(false);
    }
  }, []);

  const handleUpload = useCallback(
    (file: File) => {
      if (activeRake) {
        message.error(
          `Cannot upload a new rake while "${activeRake.rakeMaster.rrNumber || "current rake"}" is still active. Complete the active operation first.`
        );
        return false;
      }

      const latest = data.reduce<IRakeMaster | null>((acc, r) => {
        if (!r.createdAt) return acc;
        if (!acc) return r;
        return moment(r.createdAt).isAfter(acc.createdAt) ? r : acc;
      }, null);

      const sameDay =
        latest && moment(latest.createdAt).isSame(moment(), "day");

      if (sameDay && latest) {
        Modal.confirm({
          title: "Start a new rake operation?",
          content: (
            <div>
              <p style={{ marginTop: 0 }}>
                A rake was already uploaded today
                {latest.rrNumber ? ` (RR ${latest.rrNumber})` : ""} at{" "}
                {moment(latest.createdAt).format("h:mm A")}.
              </p>
              <p>
                Uploading again will start a <strong>new rake operation</strong>.
                All future weightment entries will be tied to this new rake, not
                the previous one.
              </p>
            </div>
          ),
          okText: "Start new rake",
          cancelText: "Cancel",
          onOk: () => performUpload(file),
        });
      } else {
        performUpload(file);
      }
      return false;
    },
    [activeRake, data, performUpload]
  );

  const handleDownloadReport = useCallback(async () => {
    setReportLoading(true);
    try {
      await downloadRakeReport();
      message.success("Rake report downloaded");
    } catch (error: any) {
      message.error(error?.message || "Failed to download rake report");
    } finally {
      setReportLoading(false);
    }
  }, []);

  const fetchRakes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(FETCH_ALL_RAKES);
      const payload = response?.data;
      setData(
        Array.isArray(payload) ? payload : payload?.results ?? []
      );
    } catch {
      message.error("Failed to fetch rakes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRakes();
    fetchActiveRake();
  }, [fetchActiveRake]);

  const handleDelete = useCallback(async (rakeMasterId: string) => {
    try {
      await axios.delete(DELETE_RAKE(rakeMasterId));
      message.success("Rake deleted successfully");
      await fetchRakes();
    } catch {
      message.error("Failed to delete rake");
    }
  }, []);

  const filteredData = useMemo(() => {
    if (!globalSearch) return data;
    const q = globalSearch.toLowerCase();
    return data.filter((item) =>
      Object.values(item).join(" ").toLowerCase().includes(q)
    );
  }, [data, globalSearch]);

  const stats = useMemo(() => {
    const startOfMonth = moment().startOf("month");
    const weekAgo = moment().subtract(7, "days");
    return {
      total: data.length,
      companies: new Set(data.map((r) => r.company).filter(Boolean)).size,
      thisMonth: data.filter((r) =>
        moment(r.date).isSameOrAfter(startOfMonth)
      ).length,
      last7Days: data.filter((r) =>
        moment(r.date).isSameOrAfter(weekAgo)
      ).length,
    };
  }, [data]);

  const columns = useMemo(
    () => [
      {
        title: (
          <>
            <NumberOutlined style={{ fontSize: 12 }} /> RR Number
          </>
        ),
        dataIndex: "rrNumber",
        key: "rrNumber",
        render: (rrNumber: string) =>
          rrNumber ? (
            <Text strong style={{ fontSize: 13 }}>
              {rrNumber}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <NumberOutlined style={{ fontSize: 12 }} /> FNR Number
          </>
        ),
        dataIndex: "fnrNumber",
        key: "fnrNumber",
        render: (fnrNumber: string) =>
          fnrNumber ? (
            <Text style={{ fontSize: 13 }}>{fnrNumber}</Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <BankOutlined style={{ fontSize: 12 }} /> Company
          </>
        ),
        dataIndex: "company",
        key: "company",
        render: (company: string) =>
          company ? (
            <Tag icon={<BankOutlined />} color="purple">
              {company}
            </Tag>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <CalendarOutlined style={{ fontSize: 12 }} /> Date
          </>
        ),
        dataIndex: "date",
        key: "date",
        width: 140,
        sorter: (a: IRakeMaster, b: IRakeMaster) =>
          moment(a.date).unix() - moment(b.date).unix(),
        render: (date: string) =>
          date ? (
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {moment(date).format("MMM D, YYYY")}
            </Text>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <LinkOutlined style={{ fontSize: 12 }} /> Source
          </>
        ),
        dataIndex: "s3Url",
        key: "s3Url",
        width: 100,
        render: (s3Url: string) =>
          s3Url ? (
            <a
              href={s3Url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Tag icon={<LinkOutlined />} color="blue">
                View file
              </Tag>
            </a>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              --
            </Text>
          ),
      },
      {
        title: (
          <>
            <CalendarOutlined style={{ fontSize: 12 }} /> Uploaded
          </>
        ),
        dataIndex: "createdAt",
        key: "createdAt",
        width: 140,
        sorter: (a: IRakeMaster, b: IRakeMaster) =>
          moment(a.createdAt).unix() - moment(b.createdAt).unix(),
        render: (createdAt: string) => (
          <div>
            <Text style={{ whiteSpace: "nowrap", fontSize: 13 }}>
              {moment(createdAt).format("MMM D, YYYY")}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 11 }}>
              {moment(createdAt).fromNow()}
            </Text>
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        align: "center" as const,
        render: (_: any, record: IRakeMaster) => (
          <Space size={4}>
            <Tooltip title="View details">
              <Button
                type="text"
                size="small"
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/rakes/${record.rakeMasterId}`);
                }}
              />
            </Tooltip>
            <Tooltip title="Delete rake">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  Modal.confirm({
                    title: "Delete Rake",
                    content: `Delete rake ${
                      record.rrNumber || record.rakeMasterId
                    }? This removes the master and all its wagon details.`,
                    okText: "Delete",
                    okType: "danger",
                    onOk: () => handleDelete(record.rakeMasterId),
                  });
                }}
              />
            </Tooltip>
          </Space>
        ),
      },
    ],
    [handleDelete, navigate]
  );

  return (
    <div>
      <Row justify="space-between" align="middle" className="page-header-row">
        <Col>
          <Title level={4} className="page-title">
            <ContainerOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Rake Database
          </Title>
          <Text className="page-subtitle">
            Browse rake masters and drill down into wagon-level details
          </Text>
        </Col>
        <Col>
          <Space wrap>
            <Tooltip
              title={
                activeRake
                  ? `Active rake operation in progress (${activeRake.rakeMaster.rrNumber || "current rake"}). Complete it before uploading a new rake.`
                  : undefined
              }
            >
              <Button
                icon={<UploadOutlined />}
                disabled={!!activeRake}
                onClick={() => setIsUploadModalOpen(true)}
              >
                Upload Rake
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={reportLoading}
              onClick={handleDownloadReport}
            >
              Download Report
            </Button>
            <Button icon={<ReloadOutlined />} onClick={fetchRakes}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <ActiveRakeCard onChange={() => { fetchRakes(); fetchActiveRake(); }} />

      <Row gutter={[12, 12]} style={{ marginBottom: 20 }}>
        {STAT_CARDS.map((card) => (
          <Col key={card.key} xs={12} sm={12} md={6}>
            <Card
              className="stat-card"
              style={{
                background: card.bg,
                border: `1px solid ${card.border}`,
              }}
              styles={{ body: { padding: "14px 16px" } }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    background: `${card.color}18`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18,
                    color: card.color,
                    flexShrink: 0,
                  }}
                >
                  {card.icon}
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: card.color,
                      lineHeight: 1.1,
                    }}
                  >
                    {stats[card.key as keyof typeof stats]}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "#64748b",
                      fontWeight: 500,
                      lineHeight: 1.3,
                    }}
                  >
                    {card.label}
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <div>
        <div className="table-toolbar">
          <Space wrap size={8}>
            <Input
              allowClear
              prefix={<SearchOutlined style={{ color: "#94a3b8" }} />}
              placeholder="Search rakes..."
              style={{ width: 260 }}
              value={globalSearch}
              onChange={(e) => setGlobalSearch(e.target.value)}
            />
          </Space>
        </div>

        <Table<IRakeMaster>
          rowKey="rakeMasterId"
          size="middle"
          columns={columns as any}
          dataSource={filteredData}
          loading={{ spinning: loading, tip: "Loading rakes..." }}
          onRow={(record) => ({
            onClick: () => navigate(`/rakes/${record.rakeMasterId}`),
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: (
              <div style={{ padding: "40px 0" }}>
                <ContainerOutlined
                  style={{ fontSize: 42, color: "#cbd5e1", marginBottom: 12 }}
                />
                <div style={{ color: "#64748b", fontSize: 14, fontWeight: 500 }}>
                  No rakes found
                </div>
                <div style={{ color: "#94a3b8", fontSize: 13, marginTop: 4 }}>
                  {globalSearch
                    ? "Try adjusting your search"
                    : "Upload a rake to get started"}
                </div>
              </div>
            ),
          }}
          pagination={{
            defaultPageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            showTotal: (total, range) => (
              <Text style={{ color: "#64748b", fontSize: 13 }}>
                Showing <Text strong>{range[0]}-{range[1]}</Text> of{" "}
                <Text strong>{total}</Text> rake{total !== 1 ? "s" : ""}
              </Text>
            ),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>

      <Modal
        title={
          <>
            <UploadOutlined style={{ marginRight: 8, color: "#1677ff" }} />
            Upload Rake
          </>
        }
        open={isUploadModalOpen}
        onCancel={() => {
          if (uploading) return;
          setIsUploadModalOpen(false);
        }}
        footer={null}
        width={520}
        maskClosable={!uploading}
      >
        <Space direction="vertical" size={12} style={{ width: "100%" }}>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Upload an Excel file with a sheet starting with "wagon". RR number,
            FNR number, company, date and wagon rows will be parsed and saved
            automatically.
          </Text>
          <Spin spinning={uploading} tip="Parsing — this may take a minute...">
            <Upload.Dragger
              accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              maxCount={1}
              showUploadList={false}
              disabled={uploading}
              beforeUpload={handleUpload}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Click or drag Excel file to this area to upload
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                Only .xlsx / .xls files are supported
              </p>
            </Upload.Dragger>
          </Spin>
        </Space>
      </Modal>
    </div>
  );
};

export default RakeTableComponent;
